import * as Sentry from '@sentry/capacitor';
import { TFunction } from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { RecurlyError } from '@recurly/recurly-js';
import { API } from 'api';
import { ProcessMembershipUpgradeData } from 'shared/types';

export type ProcessIapPurchaseData = {
  clientToken: string;
  externalAccountCode: string;
  email: string;
  firstName: string;
  lastName: string;
  optInToUpdates: boolean;
  isRecurring: boolean;
  planCode: string;
  /** `true` only when replaying an interrupted purchase from recovery. */
  isReconciliation: boolean;
};

/**
 * Recovery outcome for a provisioning failure:
 * - `already-provisioned`: a prior attempt succeeded; finish and finalize.
 * - `retry`: transient/unknown; keep state + receipt so a later launch retries
 *   (never strand a real Apple charge).
 */
export type IapReconcileResolution = 'already-provisioned' | 'retry';

/**
 * The code reported when an upgrade targets the plan the membership is already
 * on - the deterministic "prior attempt succeeded" signal during recovery.
 */
const UPGRADE_ALREADY_APPLIED_CODE = 'cannot-upgrade-to-same-plan';

/**
 * Creates the Watch Duty account + membership for a completed Apple IAP.
 * Called from the checkout validator (happy path) and `useReconcileInterruptedIap`.
 */
export const processIapPurchaseApi = async (
  data: ProcessIapPurchaseData,
): Promise<void> => {
  await API.post('recurly_integration/iap_create/', data);
};

/**
 * Upgrades an existing membership to Pro. Recovery must always resend the
 * originally ordered plan; a different `upgradePlanCode` would apply a new
 * upgrade rather than safely replay the interrupted one.
 */
export const upgradeMembershipApi = async (
  data: ProcessMembershipUpgradeData,
): Promise<void> => {
  await API.post('recurly_integration/upgrade_membership/', data);
};

/** First decline code from an array-shaped error body (`[code, ...]`), if any. */
const getPrimaryDeclineCode = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: any,
): string | undefined => {
  const errData = err?.response?.data;
  return errData?.length ? errData[0] : undefined;
};

export const parseRecurlyErrorToTransKey = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: any,
): string => {
  let errTranslationKey = 'recurlyBillingInformation.errors.paymentGeneric';
  if (err.response && err.response.data) {
    const errData = err.response.data;
    const declineCode = getPrimaryDeclineCode(err);
    const emailError = errData.email?.[0];
    if (
      declineCode === 'membership-exists-for-email' ||
      emailError === 'membership-exists-for-email'
    ) {
      errTranslationKey = 'common.alreadyAMember';
    } else if (emailError === 'managed-by-organization') {
      errTranslationKey = 'organization.managedAccount';
    } else if (declineCode === 'credit-card-declined') {
      errTranslationKey = 'recurlyBillingInformation.errors.ccDeclined';
    } else if (declineCode === 'bad-security-code-or-expiration') {
      errTranslationKey =
        'recurlyBillingInformation.errors.badCodeOrExpiration';
    } else if (declineCode === 'bad-zipcode') {
      errTranslationKey = 'recurlyBillingInformation.errors.badZipcode';
    } else if (declineCode === 'expired-card') {
      errTranslationKey = 'recurlyBillingInformation.errors.expiredCard';
    } else if (declineCode === 'insufficient-funds') {
      errTranslationKey = 'recurlyBillingInformation.errors.insufficientFunds';
    }
  }
  return errTranslationKey;
};

export const recurlyOnErrorHandler = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: any,
  t: TFunction,
): void => {
  const errTranslationKey = parseRecurlyErrorToTransKey(err);
  enqueueSnackbar(t(errTranslationKey), {
    variant: 'error',
    autoHideDuration: 15000,
  });
  Sentry.captureException(err);
};

export const isRecurlyError = (err: unknown): err is RecurlyError => {
  return (
    err !== null && typeof err === 'object' && 'code' in err && 'name' in err
  );
};

const getUpgradeDeclineCode = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: any,
): string | undefined =>
  err?.response?.data?.nonFieldErrors?.[0] ?? getPrimaryDeclineCode(err);

/**
 * Classifies a recovery provisioning failure. Only a deterministic
 * already-provisioned signal resolves as such; anything else is `retry` so a
 * charged purchase is never dropped.
 */
export const classifyIapReconcileError = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: any,
  isUpgrade: boolean,
): IapReconcileResolution => {
  if (isUpgrade) {
    return getUpgradeDeclineCode(err) === UPGRADE_ALREADY_APPLIED_CODE
      ? 'already-provisioned'
      : 'retry';
  }

  // A new membership whose email already has an active membership means a
  // prior interrupted attempt already provisioned it.
  return parseRecurlyErrorToTransKey(err) === 'common.alreadyAMember'
    ? 'already-provisioned'
    : 'retry';
};
